/**
 * Token di design MaeWay — allineati a **MaeDesign beta v1** (design system
 * ufficiale Maestrale Group). Palette neutra fredda, bordeaux + azzurro brand,
 * Barlow (una sola famiglia). Fonte: `MaeDesign-betav1.md` §1/§9.
 *
 * Questi valori sono l'unica fonte per colori/raggi/spazi delle schermate MaeWay
 * (desktop + mobile).
 *
 * ⚠️ **I colori qui non sono hex: sono `var(--*)`**, e il motivo è che devono seguire
 * lo schema chiaro/scuro. Prima erano costanti hex, e questi token sono letti **4796 volte
 * in 237 file** dentro `style={{}}` inline: un valore cablato in un attributo `style` non
 * cambia quando l'utente passa a scuro, mentre i componenti Mantine che gli stanno dentro
 * cambiano. Il risultato misurato era testo bianco su fondo bianco — l'avviso della Home
 * stava a **1.02 di contrasto**, cioè invisibile, e ogni pagina ne aveva da 1 a 12.
 * Indirizzando le variabili qui, tutti i 237 consumatori diventano sensibili al tema
 * **senza essere toccati**: la sostituzione avviene nel browser, a ogni cambio di schema.
 *
 * Le variabili sono definite in `theme.css` su `:root` (chiaro) e sotto
 * `[data-mantine-color-scheme="dark"]` (scuro). Il **fallback hex** dentro ogni `var()` è
 * il valore chiaro di MaeDesign: serve se il token viene letto prima che il CSS sia
 * applicato, così il default resta identico a prima.
 *
 * ⚠️ Due usi che `var()` **non** regge, e vanno evitati:
 * 1. concatenare l'alpha (`` `${mw.danger}33` `` → `var(--danger)33`, invalido): per
 *    quello ci sono i token `*Line` qui sotto, già translucidi;
 * 2. passarli a prop Mantine che fanno matematica sul colore (`Loader color=`,
 *    `Pagination color=`, `Chip color=`…): lì si usa una chiave del tema (`"brand"`,
 *    `"azzurro"`), che è già sensibile allo schema.
 * Negli attributi SVG (`<IconX color={mw.ink300} />`) invece `var()` funziona: verificato.
 */

export const mw = {
  brand: 'var(--brand, #540409)',
  /**
   * La marca **come riempimento**, con l'inchiostro che le appartiene (`--on-brand`).
   *
   * ⚠️ Non e' un sinonimo di `brand`, ed e' la distinzione che questo alias esiste per rendere
   * scrivibile. `--brand` e' il colore d'identita' dell'azienda, quello **esatto** che ha
   * scelto; `--brand-fill` e' lo stesso colore reso sicuro da dipingere sotto un testo, e in
   * tema scuro i due divergono — per la marca di casa `#540409` contro `#C1333C`.
   *
   * ⚠️ `--on-brand` e' l'inchiostro calcolato per **questo** token, non per `--brand`. Usare
   * `brand` come fondo e `--on-brand` sopra e' la coppia sbagliata, e non si vede: con una
   * marca scura il bianco ci sta sopra comunque. Con una marca chiara no — giallo `#F5C518`
   * rende 1,63:1 e celeste `#BFD8EC` 1,47:1 in tema scuro, cioe' testo invisibile. Era il caso
   * di 95 punti fino al 08-09-2026.
   *
   * Regola breve: se ci va del testo o un'icona sopra, e' `brandFill`. Se stai **mostrando** il
   * colore dell'azienda — un campione, un'anteprima — e' `brand`.
   */
  brandFill: 'var(--brand-fill, #540409)',
  brandDark: 'var(--brand-dark, #3E0105)',
  accent: 'var(--accent, #85B3D7)',
  accentText: 'var(--accent-text, #3572A3)',
  success: 'var(--success, #1B7C4E)',
  successBg: 'var(--success-bg, #DEF5E8)',
  warning: 'var(--warning, #9A6006)',
  /** Il tenue **debole**: la riga di un elenco, sotto a un badge che usa `warningBg`. */
  warningBgWeak: 'var(--warning-bg-weak, #FDF7EC)',
  warningBg: 'var(--warning-bg, #FCEFD3)',
  danger: 'var(--danger, #B91C1C)',
  dangerBg: 'var(--danger-bg, #FBE3E3)',
  dangerLine: 'var(--danger-line, rgba(185, 28, 28, 0.20))',
  warningLine: 'var(--warning-line, rgba(185, 115, 7, 0.20))',
  bg: 'var(--bg, #FFFFFF)',
  surface: 'var(--surface, #FFFFFF)',
  surface2: 'var(--surface-2, #F5F6F8)',
  line: 'var(--line, #E5E6EA)',
  brandTint: 'var(--brand-tint, #F7E8E9)',
  brandText: 'var(--brand-text, #540409)',
  ink900: 'var(--ink-900, #0F1115)',
  ink500: 'var(--ink-500, #5A5E66)',
  ink300: 'var(--ink-300, #66696E)',
  /**
   * Il grado di inchiostro fra `ink900` e `ink500`: il **corpo del testo** nei disegni
   * MaeDesign. C'era nel foglio dei token dal primo giorno e non era esposto qui, quindi chi
   * disegnava una descrizione scendeva a `ink500` — che nel foglio è il grado del testo
   * secondario, non del corpo.
   */
  ink700: 'var(--ink-700, #2A2D33)',
  /** L'inchiostro che sta **sopra** il pieno di marca. Bianco in entrambi i temi. */
  onBrand: 'var(--on-brand, #FFFFFF)',
  accentTint: 'var(--accent-tint, #F0F5F9)',
  inverseBg: 'var(--inverse-bg, #0F1115)',
  inverseFg: 'var(--inverse-fg, #FFFFFF)',
  infoBg: 'var(--info-bg, #F0F5F9)',
  /** L'assenza (ferie, permessi, malattia): un colore fisso, che il tema del tenant non ridefinisce e non si confonde con `danger`. */
  leave: 'var(--leave, #6D4BB5)',
  leaveBg: 'var(--leave-bg, #EFE9FA)',

  radiusCard: 16,
  radiusTile: 14,
  radiusPill: 12,
  radiusIcon: 12,
  /**
   * Il **pieno** dell'avvertimento: un pallino di stato, una barretta — non un inchiostro.
   * Nasce dal rename: cinque `dot:` usavano l'ex `amberInk6`, cioe' un token che si chiamava
   * «inchiostro» e riempiva. Il valore e' identico a quello che avevano, quindi non cambia un
   * pixel; cambia che ora il nome dice cosa fa, e che la regola sui pieni informativi
   * (§29, 3:1 col fondo pagina) puo' controllarlo.
   */
  warningFill: 'var(--warning-fill, #C3862D)',
} as const;

/**
 * Ombra card discreta — MaeDesign `--elev-raised` (soft/diffusa, mai dura).
 * In scuro va più profonda: un'ombra al 6% su fondo scuro non si vede, e la card perde il
 * distacco dal fondo che è tutto il suo lavoro.
 */
export const MW_CARD_SHADOW = 'var(--elev-raised, 0 4px 12px rgba(0,0,0,0.06))';
/** Ombra overlay/hero — MaeDesign `--shadow-soft` / `--elev-overlay`. */
export const MW_SHADOW_SOFT = 'var(--elev-overlay, 0 10px 26px rgba(0,0,0,0.10))';
/** Canvas desktop = surface-2 MaeDesign. */
export const MW_CANVAS = 'var(--canvas, #F5F6F8)';

/**
 * Fondo delle schermate di autenticazione. È l'unica pagina con una **sfumatura** al posto
 * di una tinta piatta, quindi non è riducibile a `mw.bg`: ha una variabile sua in
 * `theme.css`, chiara e scura.
 */
export const MW_AUTH_BG = 'var(--auth-bg, linear-gradient(160deg, #FBFBFC 0%, #F2F5F8 100%))';
/** Ombra della card di autenticazione — più ampia delle due elevazioni standard. */
export const MW_AUTH_CARD_SHADOW = 'var(--auth-card-shadow, 0 18px 44px rgba(15,17,21,0.10))';

/**
 * Font dell'interfaccia — **IBM Plex Sans** (MaeDesign v3 §4.1, 24-09-2026).
 *
 * Sostituisce Barlow, che era la famiglia della beta v1. Le due non convivono: il documento lo
 * dice e ha ragione — se una resta caricata, prima o poi qualcuno la cita.
 *
 * ⚠️ Plex era gia' stato qui, dal 25-08 al 04-09-2026, e fu riportato indietro perche' quel
 * passaggio **non era mai stato deciso**. Adesso la decisione c'e'. La storia per esteso, e la
 * ragione per cui vale la pena averla scritta, sono in `shared/styles/fonts.css`.
 *
 * ⚠️ La preoccupazione che allora resto' aperta — targhe, P.IVA, IBAN, distinguere `l` da `I` —
 * v3 la chiude in un modo che §2 non poteva: il monospaziato **sugli identificativi**, che e'
 * `MW_CODE_STYLE` qui sotto.
 */
export const MW_FONT = "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
/**
 * Font per numeri/statistiche. Resta un alias del font di interfaccia: le cifre si
 * incolonnano con `font-variant-numeric: tabular-nums`, non cambiando famiglia — ed e' anche
 * quello che dice v3 §4.1, «gli importi hanno gia' i numeri tabellari».
 */
export const MW_FONT_NUM = MW_FONT;
/**
 * COME SI RENDE UN CODICE — la forma unica, e da v3 e' monospaziata.
 *
 * Un identificativo (matricola, targa, P.IVA, id, header, percorso) ha bisogno di **cifre a
 * larghezza fissa**, perche' incolonna e si cita.
 *
 * ⚠️ **Dal 24-09-2026 porta anche la famiglia**, e prima no. MaeDesign §2 voleva una famiglia
 * sola e otteneva l'allineamento con `tabular-nums` e il peso; v3 §4.1 apre il monospaziato e
 * gli assegna esattamente questo perimetro — «un numero d'ordine, una targa, un codice fiscale,
 * un hash. Non un titolo, non un'etichetta, non un importo». Il confine e' lo stesso che questa
 * costante gia' aveva, quindi il cambio e' una riga: sono i **170 usi in 64 file** che passano
 * al mono, ed e' il cambio piu' visibile di tutto il passaggio.
 *
 * ⚠️ Il nome regge perche' dice il **ruolo** e non l'aspetto, che era il motivo per cui era
 * stato scelto cosi'. Se si fosse chiamato `MW_STILE_TABELLARE` oggi mentirebbe.
 *
 * ⚠️ Non e' una scelta nuova: era **gia' scritta** in `features/clients/ClientBits.tsx`, dove
 * la prop si chiama `mono` e non cambia affatto la famiglia — applica esattamente queste due
 * righe. Il nome mentiva, il comportamento era giusto. Nello stesso repo `features/qa/QaBits.tsx`
 * dava alla stessa prop il significato opposto (`ui-monospace`): e' la ragione per cui il
 * valore giusto va tenuto in un posto solo.
 *
 * Il nome dice il **ruolo** (un codice) e non l'aspetto (mono, tabellare): un nome che descrive
 * la resa invecchia il giorno che la resa cambia — e' la lezione di `--azzurro-text`,
   * che infatti oggi si chiama `--accent-text`: nominava l'azzurro, e in un prodotto dove
   * il colore lo sceglie l'azienda quel nome sarebbe diventato una bugia al primo tema verde.
 */
export const MW_CODE_STYLE = {
  fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '.02em',
} as const;

/**
 * Testo **macchina reso alla lettera**: un payload, un estratto di log, l'HTML di un documento,
 * uno stack d'errore. Sono i soli punti in cui il monospazio fa un lavoro che `tabular-nums`
 * non sa fare — allineare colonne su **piu' righe**, dove a variare non sono le cifre ma le
 * lettere.
 *
 * ⚠️ **Dal 24-09-2026 e' IBM Plex Mono, spedito.** Prima era il monospazio di sistema e basta,
 * perche' §2 vietava una seconda famiglia: si chiedeva alla piattaforma il suo mono, come per un
 * `<pre>`, e che fosse diverso su ogni macchina era accettabile — questi blocchi non sono
 * superficie di marca. v3 §4.1 la seconda famiglia la prevede, quindi il ripiego di sistema
 * resta in coda allo stack ma davanti c'e' la faccia che spediamo: stesso disegno su tutte le
 * macchine, senza costo, perche' il file lo carichiamo comunque per gli identificativi.
 *
 * ⚠️ Perimetro dichiarato, otto punti: l'HTML di un documento in `legals`, la riga di log e il
 * payload di `JobRunnerConsole`, la sorgente grezza di `AttendanceSourcesPage`, i `CodePanel` di
 * `DeveloperGuide`, il contenuto di `LogDetailDrawer`, l'espressione di `IndicatorsPage`, lo
 * stack di `AppErrorPage`.
 *
 * ⚠️ Erano **quattro** quando questo commento e' stato scritto la prima volta: il conto era
 * stato fatto cercando `monospace`, e non vedeva i `<Code block>` di Mantine, che il monospazio
 * lo prendevano dal tema. Un perimetro contato con una grep vale finche' la grep e' quella
 * giusta.
 *
 * Per un identificativo **in linea** si usa `MW_STILE_CODICE`. Se questo elenco cresce, la
 * domanda non e' «aggiungo un file» ma «sto rendendo un blocco di testo macchina, o sto
 * cercando il mono per un codice che sta su una riga?».
 */
export const MW_MACHINE_FONT = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";


/**
 * L'etichetta di gruppo ("IL TUO MESE"), con i numeri di MaeDesign v3 §4.5.
 *
 * ⚠️ Il maiuscolo qui e' **obbligatorio**, non decorativo: §4.5 e' `STRUTTURA` e chiude
 * l'elenco in tutti e due i sensi — tre posti in cui ci vuole (intestazioni di tabella,
 * etichette di gruppo, tag di stato) e il divieto ovunque altrove. E si **applica**, non si
 * scrive (§4.4): il testo resta in tondo nel sorgente, cosi' uno screen reader non lo compita
 * lettera per lettera e resta traducibile.
 *
 * ⚠️ La spaziatura e' in **em**, e prima era `1.2` — cioe' 1,2**px**, perche' React attacca
 * `px` a un numero nudo. Una spaziatura in px non cresce col testo: a zoom 200% le lettere
 * raddoppiano e lo spazio fra loro no, quindi la parola si stringe proprio dove serviva
 * respiro. E' lo stesso difetto delle misure in px, un livello piu' sotto.
 *
 * ⚠️ Il peso scende da 700 a **500**. §4.5 lo fissa, e ha una ragione: il maiuscolo pesa gia'
 * di suo: a 700 un'etichetta smorzata grida piu' del dato che introduce.
 */
export const mwSectionLabel: React.CSSProperties = {
  fontSize: 'var(--fs-compact)',
  fontWeight: 500,
  letterSpacing: '.11em',
  textTransform: 'uppercase',
  color: mw.ink300,
};

/** Pill valore azzurra a destra, tappabile (picker data/ora nei form). */
export function mwValuePillStyle(): React.CSSProperties {
  return {
    background: mw.accentTint,
    color: mw.accentText,
    fontWeight: 600,
    fontSize: 'var(--fs-body)',
    borderRadius: 10,
    padding: '5px 12px',
    border: 0,
    cursor: 'pointer',
    textAlign: 'right',
  };
}

/**
 * Stili per un bottom sheet che **è alto quanto il suo contenuto**.
 *
 * ## Perché serve, invece di `size="auto"`
 *
 * `size="auto"` su un `Drawer` Mantine sembra la cosa giusta e non fa niente. Mantine tratta la
 * `size` come una chiave di taglia e la traduce in `--drawer-size: var(--drawer-size-auto)`, ma
 * quella variabile **non esiste** nel foglio di stile: la catena diventa invalida, e
 * `height: var(--drawer-height, calc(100% - …))` ricade sul valore di riserva, cioè **100%**.
 * Il risultato è un foglio a tutto schermo con mezza pagina bianca sotto il contenuto —
 * esattamente il contrario di quello che il nome della prop promette.
 *
 * Qui l'altezza si scrive a mano, in linea, così vince sulla variabile qualunque cosa Mantine
 * ci metta dentro. Il tetto serve al caso opposto: una giornata con dieci timbrature non deve
 * uscire dallo schermo, deve scorrere dentro il foglio.
 */
export const mwSheetContentHeight = {
  content: { height: 'auto', maxHeight: '85dvh' },
} as const;
